/**
 * Build the payload sent to Claude / local assistant for the active page.
 */

import { defaultBidForDeal, statedAskMM } from '../engine/deskBid.js';
import { deckPricePhrase, noteCasePointer, packageDeckInputs } from '../meeting/model.js';
import { buildDrillProvenance, slimDrillForAssistant } from './drilldown.js';

function finite(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function slimDealPackage(pkg) {
  if (!pkg) return null;
  const boswell = (pkg.assets || []).find((a) => a.isBoswell);
  return {
    meta: {
      dealId: pkg.meta?.dealId,
      generatedAt: pkg.meta?.generatedAt,
      generatorVersion: pkg.meta?.generatorVersion,
      hhBase: pkg.meta?.hhBase,
      wtiBase: pkg.meta?.wtiBase,
      primaryProduct: pkg.meta?.primaryProduct,
      streamMethod: pkg.meta?.streamMethod,
      sellerAskMM: statedAskMM(pkg),
    },
    assumptions: pkg.assumptions,
    validation: pkg.validation,
    boswell: boswell
      ? {
          lease: boswell.lease,
          nri: boswell.nri,
          wi: boswell.wi,
          qiMcfd: boswell.qiMcfd,
          deAnnual: boswell.deAnnual,
          cfScale: boswell.cfScale,
          baseRealized: boswell.baseRealized,
          refPv10: boswell.ref?.pv10,
        }
      : null,
    assetCount: pkg.assets?.length ?? 0,
    scenarioIds: (pkg.scenarios || []).map((s) => s.id),
  };
}

/**
 * Stated seller ask, plus our desk bid and whether the desk edited it.
 * A missing ask stays null. The bid is never reported as the ask.
 */
export function deskBidForAssistant(dealPackage, livePageState) {
  const ask = statedAskMM(dealPackage);
  const dealId = dealPackage?.meta?.dealId || null;
  const opening = defaultBidForDeal(dealId, dealPackage, { sellerAskMM: ask });
  const live = finite(livePageState?.bidPriceMM);
  const editedFlag = livePageState?.bidEdited === true;
  if (editedFlag && live != null) {
    return { bidPriceMM: live, source: 'edited', label: null };
  }
  if (
    live != null
    && opening.bidPriceMM != null
    && Math.abs(live - opening.bidPriceMM) > 0.0005
  ) {
    return { bidPriceMM: live, source: 'edited', label: null };
  }
  const bid = live != null ? live : opening.bidPriceMM;
  if (bid == null) return { bidPriceMM: null, source: null, label: null };
  return { bidPriceMM: bid, source: 'default', label: opening.label || null };
}

function slimExecutive(exec) {
  if (!exec?.kpis) return null;
  return {
    kpis: exec.kpis,
    concentration: exec.concentration,
    meta: exec.meta,
    risks: (exec.risks || []).map((r) => ({
      title: r.title,
      severity: r.severity,
      summary: r.summary,
    })),
  };
}

/**
 * @returns {{ activeTab, label, page, dealPackage, packageSummary, livePageState, selectionContext, note }}
 */
export function buildAssistantContext({
  dashboardKey,
  dashboardLabel,
  pageData,
  dealPackage,
  executive,
  livePageState,
  selectionContext = null,
}) {
  const isCrm = dashboardKey === 'crm';
  const provenance = selectionContext
    ? buildDrillProvenance({
        dealPackage,
        pageData,
        selection: selectionContext,
        surface: dashboardKey,
      })
    : null;
  const drill = selectionContext ? slimDrillForAssistant(selectionContext, provenance) : null;
  let live =
    livePageState && typeof livePageState === 'object' ? { ...livePageState } : livePageState ?? null;
  if (drill) {
    live = live && typeof live === 'object' ? { ...live, drill } : { drill };
  }
  const sellerAsk = statedAskMM(dealPackage);
  const deskBid = deskBidForAssistant(dealPackage, live);
  const packageDeck = deckPricePhrase(packageDeckInputs(dealPackage));
  const stripPrices = live?.baseStrip;
  const baseStrip = deckPricePhrase(stripPrices);
  const financeNote = live?.financeNoteMM ?? live?.maxNoteSizeMM;
  const analysisPtr = noteCasePointer('analysis', financeNote, { strip: stripPrices });
  const financePtr = noteCasePointer('finance', live?.analysisNoteMM, { strip: stripPrices });
  const noteCases = isCrm
    ? ''
    : 'Analysis shows ABS note size, package deck (unscaled 1PDP)' +
      (packageDeck ? ` (${packageDeck})` : '') +
      '. Finance shows Max note size, base strip' +
      (baseStrip ? ` (${baseStrip})` : '') +
      `. ${analysisPtr} ${financePtr} `;
  return {
    activeTab: dashboardKey,
    label: dashboardLabel,
    sellerAskMM: sellerAsk,
    deskBid,
    note: isCrm
      ? 'Investor CRM mode. Prefer livePageState.firm / contact / outreachDraft. ' +
        'Use web research for public background. Email drafts must use **Subject:** / **Body:** blocks. ' +
        'dealPackage / packageSummary are for framing only — never invent investor-facing numbers.'
      : "Answers must be grounded in this context. `page` is the active tab JSON. " +
        "`dealPackage` is the live engine extract (when loaded). " +
        "`livePageState` is the user's current on-screen knobs/results when present. " +
        "`selectionContext` / `livePageState.drill` is the chart drilldown selection (#114) when open. " +
        'When livePageState carries a number (cases, caseValues, effectOnPdpPv10, baseVsSeller), quote it exactly ' +
        'and never re-derive IRR, MOIC or PV gaps from other fields. ' +
        'sellerAskMM is the stated seller ask, or null when none is stated. ' +
        'deskBid is our bid (source is default or edited). Never call our bid the seller ask. ' +
        'Never print those field names. Say stated seller ask and our bid. ' +
        noteCases +
        'The two notes match when the package deck is already the base strip.',
    page: pageData ?? null,
    dealPackage: slimDealPackage(dealPackage),
    packageSummary: slimExecutive(executive),
    livePageState: live,
    // Explicit seam for consumers that prefer top-level selection over livePageState.drill.
    selectionContext: drill,
  };
}
